import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { useOnError } from 'src/utils/errors';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ServicePlan } from 'src/features/servicePlans/utils/types';
import { useDeactivateServicePlan } from 'src/features/servicePlans/api/useDeactivateServicePlan';
import { SERVICE_PLAN_RESOURCE } from 'src/features/servicePlans/api/useGetServicePlan';
import { LIST_SERVICE_PLANS_RESOURCE } from 'src/features/servicePlans/api/useListServicePlans';
import { servicePlansMessages } from 'src/features/servicePlans/utils/messages';

interface DeactivateServicePlanFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedServicePlan: ServicePlan;
    children?: React.ReactNode;
}

function DeactivateServicePlanFormModal<TData>({
    selectedServicePlan,
    children,
}: DeactivateServicePlanFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();

    const onError = useOnError();

    const form = useForm({
        resolver: zodResolver(z.object({})),
    });

    const { mutate: deactivateServicePlan } = useDeactivateServicePlan({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [SERVICE_PLAN_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [LIST_SERVICE_PLANS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(servicePlansMessages.deactivateServicePlanSuccess),
                description: formatMessage(servicePlansMessages.deactivateServicePlanSuccessDescription),
            });
        },
        onError: (error) => onError(error, form.setError),
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...servicePlansMessages.deactivateServicePlan },
                subTitle: {
                    ...servicePlansMessages.deactivateServicePlanSubtitle,
                    values: {
                        name: selectedServicePlan.name,
                    },
                },
                icon: ICON_MAP.TOGGLE_OFF,
                confirmationMessage: { ...servicePlansMessages.confirmDeactivateServicePlan },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: servicePlansMessages.deactivate,
                callbackFn: () => {
                    return (
                        deactivateServicePlan({
                            planId: selectedServicePlan.id,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default DeactivateServicePlanFormModal;
